import { action, makeObservable, observable } from 'mobx';
import { useEffect } from 'react';
import { API } from '~/api';
import { ApiService } from '~/service/service.api';
import { UIService } from '~/service/service.ui';
import { FeedbackReplyDto, FeedbackStatus } from '~/shared/dto/admin/feedback.dto';
import { Model } from '~/shared/service/service.base';
import { Async } from '~/shared/tools/async';
import { MenuModel } from './model.menu';

@Model()
export class FeedbackDrawerModel {
	readonly replyTitleMenu = new MenuModel();
	readonly replyMessageMenu = new MenuModel();

	readonly async = new Async();
	readonly asyncSimilar = new Async();

	@observable replyDialogVisible = false;

	@observable.ref selectedItem?: API.Feedback = undefined;

	@observable.ref similar: API.FeedbackSuggestionSimilar = {};
	@observable.ref similarBrand?: API.FeedbackSimilarBrand = undefined;
	@observable.ref similarFragrance?: API.FeedbackSimilarFragrance = undefined;

	constructor(
		private api: ApiService,
		private ui: UIService,
		public reloadList: () => void,
	) {
		makeObservable(this);
	}

	@action.bound setSelectedItem(item: API.Feedback | undefined) {
		if (this.selectedItem?.id !== item?.id) {
			this.similarBrand = undefined;
			this.similarFragrance = undefined;
			this.similar = {};
		}
		this.selectedItem = item;
	}

	@action.bound showReplyDialog() {
		this.replyDialogVisible = true;
	}

	@action.bound hideReplyDialog() {
		this.replyDialogVisible = false;
	}

	@action.bound handleChangeSimilarBrand(_: unknown, brand: API.FeedbackSimilarBrand | undefined) {
		this.similarBrand = brand || undefined;
	}

	@action.bound handleChangeSimilarFragrance(_: unknown, fragrance: API.FeedbackSimilarFragrance | undefined) {
		this.similarFragrance = fragrance || undefined;
	}

	resolveSelectedItem = () => {
		this.changeStatus('DONE');
	};

	reopenSelectedItem = () => {
		this.changeStatus('NEW');
	};

	declineSelectedItem = () => {
		this.changeStatus('DECLINED');
	};

	replyToSelected = (dto: FeedbackReplyDto) => {
		this.async.call(async () => {
			if (!this.selectedItem) return;
			const item = await this.api.feedbackReply(this.selectedItem.id, dto);
			localStorage.setItem(
				'feedback-reply-' + item.data.type,
				JSON.stringify({
					title: dto.title,
					message: dto.message,
					description: dto.description,
					imgLeft: dto.imgLeft,
					imgRight: dto.imgRight,
				}),
			);
			this.reloadList();
			return () => {
				this.selectedItem = item.data;
				this.replyDialogVisible = false;
			};
		});
	};

	useSimilarFragrances = () => {
		const id = this.selectedItem?.id;
		useEffect(() => {
			if (!id) return;
			this.asyncSimilar.call(async () => {
				const { data } = await this.api.feedbackGetSuggestionSimilar(id);
				return () => (this.similar = data);
			});
		}, [id]);

		return this.similar;
	};

	getLastReply = () => {
		if (!this.selectedItem) return undefined;
		try {
			return JSON.parse(localStorage.getItem('feedback-reply-' + this.selectedItem.type) ?? '{}');
		} catch {
			return undefined;
		}
	};

	downloadFile = () => {
		const item = this.selectedItem;
		if (!item?.fileId) return;

		this.async.call(async () => {
			const resp = await this.api.axios.get(`/feedback/${item.id}/file`, { responseType: 'blob' });
			this.ui.storeFile({
				content: resp.data,
				type: resp.headers['content-type'] ?? 'application/octet-stream',
				name: this.parseFileName(resp.headers['content-disposition']) ?? item.message,
			});
		});
	};

	private parseFileName(disposition: string | undefined) {
		const utf8 = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
		if (utf8) return decodeURIComponent(utf8[1]);
		return disposition?.match(/filename="([^"]+)"/i)?.[1];
	}

	protected changeStatus(status: FeedbackStatus) {
		this.async.call(async () => {
			if (!this.selectedItem) return;
			const item = await this.api.feedbackUpdateStatus(this.selectedItem.id, { status });
			this.reloadList();
			return () => {
				this.selectedItem = item.data;
			};
		});
	}
}
